import type { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { getTRPCClient, PERFORMANCE_MEASURE_DEFINITION_KIND, syncKey } from "@repo/api";
import type {
  CloseOutPerformanceMeasureInput,
  GradePerformanceMeasureInput,
  PerformanceMeasureInput,
  StartPerformanceMeasureInput,
} from "@repo/api/schemas";
import {
  asPerformanceMeasureId,
  computeQuestionSetScores,
  hasOptionPoints,
  type PatientId,
  type PerformanceMeasureFacts,
  type PerformanceMeasureId,
  type PerformanceMeasureOutcome,
  scoreItemsRange,
  TOTAL_SCORE_ID,
  type UnitValue,
  type VisitId,
} from "@repo/domain";
import type { Collection, InitialQueryBuilder } from "@tanstack/db";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { deriveQuestionnaireInstanceFields } from "../fhir/collections";
import {
  isPerformanceMeasureRow,
  readPerformanceMeasureRow,
  writePerformanceMeasureRow,
} from "../fhir/performance-measure-storage";
import {
  readPerformanceMeasureDefinitionRow,
  type StoredQuestionnaireDefinition,
} from "../fhir/questionnaire-definition-storage";
import type { QuestionnaireInstanceRow, QuestionnaireRow } from "../fhir/types";
import type { PerformanceMeasureScoreDto } from "./performance-measure-scores";

export interface PerformanceMeasureDto extends PerformanceMeasureFacts {
  content: StoredQuestionnaireDefinition;
  graded: number;
  total: number;
  ungraded: { taskId: string; label: string; sectionId: string }[];
  headerScores: PerformanceMeasureHeaderScore[];
}

export interface PerformanceMeasureHeaderScore {
  scoreId: string;
  label: string;
  value: number;
  maximum: number;
  band?: string;
  unit?: UnitValue;
}

export function performanceMeasureHeaderScores(
  measure: Pick<PerformanceMeasureDto, "id" | "outcome" | "headerScores">,
  scores: readonly PerformanceMeasureScoreDto[],
): PerformanceMeasureHeaderScore[] {
  return measure.headerScores.map((preview) => {
    const recorded = scores.find((score) => score.measureId === measure.id && score.scoreId === preview.scoreId);
    return measure.outcome === "performed" && recorded?.value === preview.value
      ? { ...preview, value: recorded.value, unit: recorded.unit, band: recorded.band }
      : preview;
  });
}

export function projectPerformanceMeasure(
  row: QuestionnaireResponse,
  definition: Questionnaire,
): PerformanceMeasureDto {
  const facts = readPerformanceMeasureRow(row);
  const content = readPerformanceMeasureDefinitionRow(definition);
  if (content.url !== facts.definition.url || String(content.version) !== facts.definition.version)
    throw new Error("Performance measure definition pin mismatch");
  const tasks = content.sections.flatMap((section) =>
    section.items.flatMap((item) => (item.kind === "Question" ? [{ item, section }] : [])),
  );
  const ungraded = tasks
    .filter(({ item }) => !facts.grades.some((grade) => grade.task === item.id))
    .map(({ item, section }) => ({ taskId: item.id, label: section.label, sectionId: section.id }));
  const complete = tasks.length > 0 && ungraded.length === 0 && (!facts.outcome || facts.outcome === "performed");
  const results = complete
    ? computeQuestionSetScores(
        content.sections,
        content.scores,
        facts.grades.map((grade) => ({ questionId: grade.task, value: grade.answer })),
      )
    : [];
  const qualifyingScores = results.flatMap((result) => {
    const score = content.scores.find((score) => score.id === result.scoreId);
    if (!score) return [];
    const items = tasks.map(({ item }) => item).filter((item) => score.itemIds.includes(item.id));
    if (!score.calculation && score.itemIds.length <= 1 && !items.some(hasOptionPoints)) return [];
    return [{ ...result, label: score.label, maximum: scoreItemsRange(items, score.calculation).maximum }];
  });
  const totalScore = qualifyingScores.find((score) => score.scoreId === TOTAL_SCORE_ID);
  const headerScores = totalScore ? [totalScore] : qualifyingScores;
  return { ...facts, content, graded: tasks.length - ungraded.length, total: tasks.length, ungraded, headerScores };
}

export function buildPerformanceMeasuresQuery(
  query: InitialQueryBuilder,
  responses: Collection<QuestionnaireInstanceRow, string>,
  definitions: Collection<QuestionnaireRow, string>,
  filters: { patientId: PatientId; visitId?: VisitId },
) {
  const candidates = query.from({ row: responses }).where(({ row }) => eq(row.patientId, filters.patientId));
  const selected = filters.visitId ? candidates.where(({ row }) => eq(row.visitId, filters.visitId)) : candidates;
  return selected.fn
    .where(({ row }) => isPerformanceMeasureRow(row))
    .innerJoin({ definition: definitions }, ({ row, definition }) => eq(row.definitionUrl, definition.url))
    .where(({ row, definition }) => eq(row.definitionVersion, definition.version))
    .where(({ definition }) => eq(definition.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND))
    .orderBy(({ row }) => row.instanceAssignedAt, "asc")
    .fn.select(({ row, definition }) => ({ measure: projectPerformanceMeasure(row, definition) }));
}
/** Undefined scope queries nothing, so a caller on every page can pass it off its own route. */
export function usePerformanceMeasures(
  filters: { patientId: PatientId; visitId?: VisitId } | undefined,
): PerformanceMeasureDto[] {
  const responses = useCollection("questionnaireResponse");
  const definitions = useCollection("questionnaire");
  const result = useLiveQuery(
    (q) => (filters ? buildPerformanceMeasuresQuery(q, responses, definitions, filters) : undefined),
    [responses, definitions, filters?.patientId, filters?.visitId],
  );
  return (result.data ?? []).map(({ measure }) => measure);
}
export function usePerformanceMeasure(
  filters: { patientId: PatientId; measureId: PerformanceMeasureId } | undefined,
): PerformanceMeasureDto | undefined {
  return usePerformanceMeasures(filters ? { patientId: filters.patientId } : undefined).find(
    (measure) => measure.id === filters?.measureId,
  );
}

const responses = () => getDataStoreClient().getCollection("questionnaireResponse");
function row(id: PerformanceMeasureId) {
  const row = responses().get(id);
  if (!row) throw new Error(`Performance measure '${id}' is not loaded`);
  return row;
}
function update(id: PerformanceMeasureId, change: (facts: PerformanceMeasureFacts) => PerformanceMeasureFacts) {
  const prior = row(id);
  responses().update(syncKey(prior), (draft) => {
    Object.assign(draft, writePerformanceMeasureRow(change(readPerformanceMeasureRow(prior)), prior));
  });
}
export const startPerformanceMeasure = defineWrite<StartPerformanceMeasureInput & { patientId: PatientId }>({
  optimistic(input) {
    const resource = writePerformanceMeasureRow({
      id: asPerformanceMeasureId(input.id),
      patient: input.patientId,
      visit: input.visitId,
      clinician: input.clinician,
      recordedAt: new Date().toISOString(),
      definition: { url: input.definitionUrl, version: String(input.expectedVersion) },
      status: "open",
      grades: [],
    });
    responses().insert({ ...resource, ...deriveQuestionnaireInstanceFields(resource) });
  },
  persist: (input, options) => getTRPCClient().performanceMeasure.start.mutate(input, options),
});
function grade(input: GradePerformanceMeasureInput) {
  update(input.measureId, (facts) => ({
    ...facts,
    grades: [...facts.grades.filter((grade) => grade.task !== input.task), { task: input.task, answer: input.answer }],
  }));
}
export const gradePerformanceMeasure = defineWrite<GradePerformanceMeasureInput>({
  optimistic: grade,
  persist: (input, options) => getTRPCClient().performanceMeasure.grade.mutate(input, options),
});
export const editPerformanceMeasureGrade = defineWrite<GradePerformanceMeasureInput>({
  optimistic: grade,
  persist: (input, options) => getTRPCClient().performanceMeasure.editGrade.mutate(input, options),
});
function outcome(input: PerformanceMeasureInput, outcome: PerformanceMeasureOutcome) {
  update(input.measureId, (facts) => ({ ...facts, outcome }));
}
export const closeOutPerformanceMeasure = defineWrite<CloseOutPerformanceMeasureInput>({
  optimistic: (input) =>
    update(input.measureId, (facts) => ({
      ...facts,
      status: "closed",
      outcome: input.outcome ?? facts.outcome ?? "performed",
    })),
  persist: (input, options) => getTRPCClient().performanceMeasure.closeOut.mutate(input, options),
});
export const markPerformanceMeasureDidNotPerform = defineWrite<PerformanceMeasureInput>({
  optimistic: (input) => outcome(input, "did-not-perform"),
  persist: (input, options) => getTRPCClient().performanceMeasure.markDidNotPerform.mutate(input, options),
});
export const markPerformanceMeasureUnableToPerform = defineWrite<PerformanceMeasureInput>({
  optimistic: (input) => outcome(input, "unable-to-perform"),
  persist: (input, options) => getTRPCClient().performanceMeasure.markUnableToPerform.mutate(input, options),
});

export const clearPerformanceMeasureOutcome = defineWrite<PerformanceMeasureInput>({
  optimistic: (input) => update(input.measureId, (facts) => ({ ...facts, outcome: undefined })),
  persist: (input, options) => getTRPCClient().performanceMeasure.clearOutcome.mutate(input, options),
});
